import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { ArrowUpRight, Calendar, MapPin, Sparkles } from "lucide-react";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events")
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const { upcoming, past } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const u = [];
    const p = [];
    events.forEach((e) => {
      if (e.recurring) { u.push(e); return; }
      if (!e.event_date) { u.push(e); return; }
      if (e.event_date >= today) u.push(e); else p.push(e);
    });
    // Preserve DB order (admin controls the sequence via ↑ / ↓ in admin).
    return { upcoming: u, past: p };
  }, [events]);

  return (
    <div data-testid="events-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="overline mb-4">Join us in person</div>
      <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-4">Events</h1>
      <p className="text-[var(--ink-soft)] text-lg max-w-2xl leading-relaxed mb-14">
        Meetings, hikes, campfire nights, and every stop on the road where recovery happens
        in real life. Come sit with us.
      </p>

      {loading ? (
        <div className="text-[var(--ink-soft)] text-sm">Loading…</div>
      ) : events.length === 0 ? (
        <div className="text-center py-24 text-[var(--ink-soft)]">
          <Sparkles className="w-8 h-8 mx-auto mb-4 opacity-40" strokeWidth={1.25} />
          <p>No events posted yet — we&apos;re still lining them up.</p>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <section className="mb-16">
              <div className="overline mb-6">Upcoming &amp; recurring</div>
              <div className="grid md:grid-cols-2 gap-6">
                {upcoming.map((e) => <EventCard key={e.id} e={e} />)}
              </div>
            </section>
          )}
          {past.length > 0 && (
            <section>
              <div className="overline mb-6">In the rearview</div>
              <div className="grid md:grid-cols-2 gap-6 opacity-90">
                {past.map((e) => <EventCard key={e.id} e={e} muted />)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function EventCard({ e, muted = false }) {
  return (
    <article
      data-testid={`event-${e.id}`}
      className={`group relative overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--bg)] hover:-translate-y-1 transition-all duration-300 ${muted ? "grayscale-[.15]" : ""}`}
    >
      {e.image_url && (
        <div className="aspect-[16/9] overflow-hidden">
          <img src={e.image_url} alt={e.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        </div>
      )}
      <div className="p-7">
        <div className="flex items-center gap-4 text-xs font-mono-acc text-[var(--ink-soft)] mb-3">
          {e.recurring ? (
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Recurring</span>
          ) : e.event_date ? (
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{e.event_date}</span>
          ) : null}
          {e.location && (
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{e.location}</span>
          )}
        </div>
        <h3 className="font-serif-display text-2xl md:text-3xl mb-3">{e.title}</h3>
        {e.description && (
          <p className="text-[var(--ink-soft)] leading-relaxed mb-5">{e.description}</p>
        )}
        {e.cta_url && (
          <a href={e.cta_url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--forest)] hover:underline">
            {e.cta_label || "Learn more"} <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </article>
  );
}
